import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { SiteShell } from "@/components/site/SiteShell";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { SectionHeading } from "@/components/site/SectionHeading";
import { webPageScript } from "@/lib/webpage-jsonld";


export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service | Online Tax VAT Bangladesh" },
      { name: "description", content: "Terms governing Online Tax VAT Bangladesh services, including scope, fees, refunds, responsibilities and support." },
      { property: "og:title", content: "Terms of Service — Online Tax VAT Bangladesh" },
      { property: "og:description", content: "Terms governing use of Online Tax VAT Bangladesh services." },
      { property: "og:url", content: "https://onlinetaxvat.com/terms" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/terms" }],
    scripts: [webPageScript({
      path: "/terms",
      name: "Terms of Service — Online Tax VAT Bangladesh",
      description: "Terms governing Online Tax VAT Bangladesh services, including scope, fees, refunds, responsibilities and support.",
    })],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <SiteShell>
      <section className="container-page py-12">
        <h1 className="sr-only">Terms of Service — Online Tax VAT Bangladesh</h1>
        <SectionHeading
          eyebrow="Legal"
          title="Terms of Service"
          description="Last updated: 16 June 2026"
        />
        <article className="prose prose-slate dark:prose-invert mt-8 max-w-3xl">
          <p>
            These Terms of Service (&ldquo;Terms&rdquo;) govern your use of the Online
            Tax VAT Bangladesh website and the VAT, BIN, TIN, Mushak 9.1 and Income
            Tax return services we deliver to individuals, freelancers, garments
            factories, SMEs and corporate clients across Bangladesh. By creating an
            account, uploading documents or paying an invoice, you accept these Terms
            in full. Please also review our <Link to="/privacy">Privacy Policy</Link>,
            our <Link to="/faq">FAQ</Link>, the published <Link to="/packages">pricing
            packages</Link> and the full <Link to="/services">service catalogue</Link> before
            proceeding. These Terms exist alongside the National Board of Revenue (NBR)
            VAT &amp; Supplementary Duty Act 2012 and the Income Tax Act 2023 — nothing
            in this document overrides those statutory rules.
          </p>
          <h2>1. Services</h2>
          <p>
            Online Tax VAT Bangladesh provides VAT registration (BIN), VAT return
            (Mushak 9.1), TIN certificates, Income Tax return preparation, visa tax
            files, payroll tax computation, audit-ready balance sheet preparation and
            related advisory services for individuals and businesses in Bangladesh. The
            specific scope of work for your engagement is defined by the package you
            purchase on the <Link to="/packages">Packages</Link> page or the custom quote
            shared by our consultant.
          </p>
          <h2>2. Client responsibilities</h2>
          <p>
            You agree to provide accurate, complete and legible documents (NID, TIN,
            BIN, trade licence, bank statements, sales registers and supporting
            invoices) within the timelines we share with you. We are not liable for
            interest, penalties or late fees that result from incorrect or delayed
            information supplied by the client, nor for filings that are rejected by
            NBR because the underlying source documents are missing.
          </p>
          <h2>3. Fees &amp; payment</h2>
          <p>
            Service fees are listed on our <Link to="/packages">Packages</Link> page and
            are payable in full before delivery unless otherwise agreed in writing.
            Government fees, treasury challans, stamp duty and NBR portal charges are
            quoted separately and reimbursed at cost. Invoices are issued
            electronically and remain valid for seven calendar days from the date of
            issue.
          </p>
          <h2>4. Refund policy</h2>
          <p>
            Once filing work has begun, fees are non-refundable because consultant time
            and government portal submissions cannot be reversed. Pre-work
            cancellations qualify for a full refund minus payment-gateway processing
            charges. Refund requests must be made in writing to our support team via
            the <Link to="/contact">Contact</Link> page within seven days of payment.
          </p>
          <h2>5. Confidentiality</h2>
          <p>
            All client information is treated as confidential and handled per our
            <Link to="/privacy"> Privacy Policy</Link>. Documents are accessible only to
            the consultants assigned to your case and the NBR officers who legally
            require them for verification.
          </p>
          <h2>6. Limitation of liability</h2>
          <p>
            Our aggregate liability for any claim arising out of or in connection with
            a service is limited to the fees paid for that specific service. We are
            not responsible for indirect, consequential or punitive losses, including
            loss of profit, loss of business reputation, or third-party penalties
            outside our control.
          </p>
          <h2>7. Governing law</h2>
          <p>
            These terms are governed by the laws of the People&apos;s Republic of
            Bangladesh, and any dispute arising under them is subject to the exclusive
            jurisdiction of the competent courts located in Dhaka.
          </p>
          <h2>8. Changes to these terms</h2>
          <p>
            We may revise these Terms from time to time to reflect changes in NBR
            regulations or our internal procedures. The latest version will always be
            published at this URL with a refreshed &ldquo;Last updated&rdquo; date.
            Continued use of the service after a revision means you accept the updated
            Terms.
          </p>
          <h2>Related reading</h2>
          <ul>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/faq">Frequently Asked Questions</Link></li>
            <li><Link to="/services">All VAT &amp; Tax services</Link></li>
            <li><Link to="/knowledge-hub">Knowledge Hub — guides &amp; circulars</Link></li>
            <li><Link to="/blog">Latest blog updates</Link></li>
            <li><Link to="/contact">Contact our team</Link></li>
          </ul>
        </article>
        <div className="container-page"><ServiceDetailSection content={SERVICE_DETAIL_CONTENT["terms"]} /></div>
      </section>
      <HiddenSeoContext topic="terms" />
    </SiteShell>
  );
}
